import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  BrainCircuit,
  CircuitBoard,
  CloudCog,
  Database,
  Gauge,
  GitMerge,
  Layers3,
  RadioTower,
  ScanLine,
  ShieldCheck,
  Workflow
} from "lucide-react";

export type Service = {
  slug: string;
  eyebrow: string;
  title: string;
  shortTitle: string;
  description: string;
  cardTitle: string;
  intro: string;
  icon: LucideIcon;
  recognition: string[];
  capabilities: Array<{ title: string; text: string; icon: LucideIcon }>;
  outcomes: string[];
  approachTitle: string;
  approach: string;
  ctaTitle: string;
  ctaText: string;
};

export const services: Service[] = [
  {
    slug: "data-ai",
    eyebrow: "Data & AI",
    title: "Maak van data een stuurmiddel.",
    shortTitle: "Data & AI",
    description: "Breng gegevens samen, maak prestaties zichtbaar en zet AI alleen in waar het echt iets toevoegt.",
    cardTitle: "Van losse data naar bruikbaar inzicht",
    intro: "Data levert pas waarde op als die betrouwbaar, toegankelijk en begrijpelijk is. STAQL helpt je van versnipperde bronnen naar heldere inzichten — en zet analytics of AI in wanneer dat aantoonbaar iets verbetert.",
    icon: BarChart3,
    recognition: [
      "Belangrijke informatie staat verspreid over spreadsheets en systemen.",
      "Rapportages kosten iedere week of maand opnieuw veel handwerk.",
      "Verschillende cijfers vertellen verschillende verhalen.",
      "Je hebt dashboards, maar ze helpen onvoldoende bij beslissingen.",
      "Er zijn ideeën voor AI, maar de praktische waarde is nog onduidelijk.",
      "De huidige dataomgeving remt nieuwe plannen af."
    ],
    capabilities: [
      { title: "Datafundament", text: "Databronnen koppelen, data structureren en een schaalbare cloud- of open-source omgeving inrichten.", icon: Database },
      { title: "Business intelligence", text: "Dashboards en rapportages ontwikkelen waarmee gebruikers sneller zien wat aandacht vraagt.", icon: Gauge },
      { title: "Data science & AI", text: "Praktische toepassingen voor voorspelling, documentverwerking en slimme ondersteuning.", icon: BrainCircuit },
      { title: "Datakwaliteit & beheer", text: "Afspraken, controles en toegang zo inrichten dat cijfers betrouwbaar en beheersbaar blijven.", icon: ShieldCheck }
    ],
    outcomes: [
      "Minder tijd kwijt aan verzamelen en corrigeren",
      "Eén duidelijker beeld van prestaties en afwijkingen",
      "Snellere en beter onderbouwde beslissingen",
      "Herbruikbare data voor nieuwe automatiseringen",
      "Praktische AI in plaats van een vrijblijvende proef"
    ],
    approachTitle: "Klein genoeg om te toetsen, sterk genoeg om op door te bouwen",
    approach: "We starten bij de beslissing of het proces dat beter moet. Waar mogelijk toetsen we de waarde met een afgebakende eerste versie. Werkt die, dan bouwen we gericht verder aan betrouwbaarheid, gebruik en schaal.",
    ctaTitle: "Meer halen uit de data die je al hebt?",
    ctaText: "In een eerste gesprek brengen we bronnen, knelpunten en gewenste inzichten op hoofdlijnen in kaart."
  },
  {
    slug: "automatisering-software",
    eyebrow: "Automatisering & software",
    title: "Stop met werk dat een systeem kan doen.",
    shortTitle: "Automatisering & software",
    description: "Automatiseer processen, verbind applicaties en bouw software die aansluit op hoe jouw bedrijf werkt.",
    cardTitle: "Laat systemen het terugkerende werk doen",
    intro: "Terugkerend invoerwerk, losse applicaties en omslachtige controles kosten tijd en aandacht. STAQL automatiseert processen, verbindt systemen en bouwt software die past bij hoe jouw bedrijf werkelijk werkt.",
    icon: Workflow,
    recognition: [
      "Gegevens worden meerdere keren overgenomen.",
      "Een proces hangt af van losse mails en spreadsheets.",
      "Medewerkers moeten informatie handmatig controleren of samenvoegen.",
      "Bestaande software ondersteunt nét niet wat nodig is.",
      "Klanten missen een goede digitale ingang.",
      "Een sterk idee komt niet verder door gebrek aan technische uitvoering."
    ],
    capabilities: [
      { title: "Procesautomatisering", text: "Terugkerende handelingen, controles, meldingen en overdrachten automatisch laten verlopen.", icon: Workflow },
      { title: "Integraties", text: "Applicaties, databases en API’s veilig laten communiceren, zonder handmatig gegevens te verplaatsen.", icon: GitMerge },
      { title: "Websoftware", text: "Interne tools, klantportalen, websites en webapplicaties met een heldere gebruikerservaring.", icon: Layers3 },
      { title: "Digitale producten", text: "Een idee technisch verkennen en stap voor stap ontwikkelen tot een bruikbaar product.", icon: CloudCog }
    ],
    outcomes: [
      "Minder repetitief werk en minder invoerfouten",
      "Kortere doorlooptijden",
      "Betere overdracht tussen mensen en systemen",
      "Meer grip op uitzonderingen en voortgang",
      "Een digitale oplossing die kan meegroeien"
    ],
    approachTitle: "Niet automatiseren om het automatiseren",
    approach: "We automatiseren alleen wat stabiel genoeg is en werkelijk voordeel oplevert. Soms is een kleine procesverbetering slimmer dan nieuwe software. Soms vraagt het probleem juist om maatwerk. Je krijgt een onderbouwde keuze, geen vooraf gekozen tool.",
    ctaTitle: "Welk werk wil je niet langer handmatig doen?",
    ctaText: "Laat zien hoe het nu gaat. Dan zoeken we naar de kortste route naar een betere werkwijze."
  },
  {
    slug: "iot-slimme-systemen",
    eyebrow: "IoT & slimme systemen",
    title: "Meten wat gebeurt. Automatiseren wat beter kan.",
    shortTitle: "IoT & slimme systemen",
    description: "Verbind apparaten, verzamel realtime gegevens en automatiseer monitoring, energie en technische processen.",
    cardTitle: "Maak de fysieke wereld meetbaar en slim",
    intro: "STAQL verbindt sensoren, apparaten, software en data tot slimme systemen. Zo krijg je realtime inzicht in fysieke processen en kun je gericht reageren, besparen of automatiseren.",
    icon: RadioTower,
    recognition: [
      "Je wilt realtime zicht op wat er op locatie gebeurt.",
      "Energie of verbruik moet nauwkeuriger worden gemeten.",
      "Apparaten moeten automatisch een melding of actie starten.",
      "Data van verschillende sensoren moet op één plek samenkomen.",
      "Je wilt de technische haalbaarheid toetsen vóór een grote investering."
    ],
    capabilities: [
      { title: "Energie & verbruik", text: "Verbruik zichtbaar maken, afwijkingen herkennen en ontdekken waar verbetering mogelijk is.", icon: Gauge },
      { title: "Realtime monitoring", text: "De status van apparaten, ruimtes of processen volgen en gericht meldingen ontvangen.", icon: ScanLine },
      { title: "Slimme gebouwen", text: "Voorzieningen beter laten samenwerken op basis van actuele informatie.", icon: CircuitBoard },
      { title: "Maatwerk-IoT", text: "Hardware, connectiviteit, software en dashboards als één betrouwbare keten ontwikkelen.", icon: RadioTower }
    ],
    outcomes: [
      "Realtime zicht op processen en verbruik",
      "Snellere signalering van afwijkingen",
      "Minder handmatige controles op locatie",
      "Data voor gerichte besparing of onderhoud",
      "Een onderbouwde basis voor verdere opschaling"
    ],
    approachTitle: "Van proefopstelling naar betrouwbare toepassing",
    approach: "Bij IoT moet de hele keten kloppen: meting, verbinding, verwerking, beveiliging en gebruik. We maken die keten inzichtelijk, toetsen kritieke aannames en bouwen eerst wat nodig is om waarde en betrouwbaarheid te bewijzen.",
    ctaTitle: "Een apparaat, ruimte of proces slimmer maken?",
    ctaText: "Vertel wat je wilt meten of automatiseren. STAQL helpt de technische en zakelijke haalbaarheid bepalen."
  }
];
